import React from 'react';
import { Home, Landmark, Users, Info } from 'lucide-react';

/**
 * Компонент раздела формы для параметров имущественного вычета
 */
const PropertyDeductionSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  return (
    <div className="mb-6 bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-200 dark:border-emerald-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Home className="w-5 h-5 mr-2 text-emerald-500" />
        Параметры имущественного вычета
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Стоимость недвижимости */}
        <div>
          <label htmlFor="propertyPrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Стоимость недвижимости
          </label>
          <input
            type="text"
            id="propertyPrice"
            name="propertyPrice"
            value={formData.propertyPrice}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.propertyPrice ? 'input-error' : ''}`}
            placeholder="Введите полную стоимость"
          />
          {errors.propertyPrice && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.propertyPrice}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            Максимальная сумма к вычету: 2 000 000 ₽ (возврат до 260 000 ₽)
          </p>
        </div>

        {/* Включить вычет по ипотечным процентам */}
        <div className="flex items-start mt-2">
          <div className="flex items-center h-5">
            <input
              id="includeInterestDeduction"
              name="includeInterestDeduction"
              type="checkbox"
              checked={formData.includeInterestDeduction}
              onChange={handleInputChange}
              className="form-checkbox"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="includeInterestDeduction" className="font-medium text-gray-700 dark:text-gray-300">
              Включить вычет по ипотечным процентам
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Дополнительный вычет при покупке в ипотеку
            </p>
          </div>
        </div>

        {/* Сумма уплаченных процентов по ипотеке */}
        {formData.includeInterestDeduction && (
          <div className="col-span-full md:col-span-1">
            <label htmlFor="mortgageInterest" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Landmark className="w-4 h-4 mr-2 text-emerald-500" />
              Сумма уплаченных процентов
            </label>
            <input
              type="text"
              id="mortgageInterest"
              name="mortgageInterest"
              value={formData.mortgageInterest}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.mortgageInterest ? 'input-error' : ''}`}
              placeholder="Введите сумму процентов"
            />
            {errors.mortgageInterest && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.mortgageInterest}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Максимальная сумма к вычету: 3 000 000 ₽ (возврат до 390 000 ₽)
            </p>
          </div>
        )}

        {/* Учет супруга/супруги */}
        <div className="flex items-start mt-2">
          <div className="flex items-center h-5">
            <input
              id="includeSpouse"
              name="includeSpouse"
              type="checkbox"
              checked={formData.includeSpouse}
              onChange={handleInputChange}
              className="form-checkbox"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="includeSpouse" className="font-medium text-gray-700 dark:text-gray-300">
              Учитывать супруга/супругу при расчете
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Совместное получение вычета при общей собственности
            </p>
          </div>
        </div>

        {/* Годовой доход супруга/супруги */}
        {formData.includeSpouse && (
          <div className="col-span-full md:col-span-1">
            <label htmlFor="spouseIncome" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Users className="w-4 h-4 mr-2 text-emerald-500" />
              Годовой доход супруга/супруги
            </label>
            <input
              type="text"
              id="spouseIncome"
              name="spouseIncome"
              value={formData.spouseIncome}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.spouseIncome ? 'input-error' : ''}`}
              placeholder="Введите годовой доход"
            />
            {errors.spouseIncome && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.spouseIncome}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Информационный блок */}
      <div className="mt-4 bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              Об имущественном вычете
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Имущественный налоговый вычет можно получить один раз в жизни при покупке жилья (квартиры, дома, земельного участка).
              Максимальная сумма вычета - 2 млн ₽, а по процентам ипотеки - 3 млн ₽.
              Супруги могут распределить вычет между собой, что ускорит возврат налога.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDeductionSection;